import { datadogRum } from "@datadog/browser-rum";
import { getReportLifecycleStatus } from "@utils/reportStatus";
import { templateHasConclusionsFields } from "@utils/reportTemplateSections";
import { downloadFileBlob, nonNullable } from "@utils/utils";
import React from "react";
import { FormProvider } from "react-hook-form";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";

import { Badge, Button, Paragraph, SkeletonLoader, TrainStopProgressNav } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportStatusChoices,
  ApiReportTemplateExportFrameworkChoices,
  AssessmentToolType,
  ExportTypeEnum,
  ReportSectionsType,
  ReportStatusEnums,
  ReportType,
  ReportTypeEnums,
  useAssessmentToolsQuery,
  useGetExportReportDocumentQuery,
  useUpdateReportMutation,
} from "@api/graphql/types-and-hooks";
import { StatusType } from "@components/ClinicalReportsTable/ClinicalReportsTable";
import ClinicalReportExport from "@components/PDFGenerator/ClinicalReportExport";
import ExportWithTemplateV2 from "@components/PDFGenerator/ExportWithTemplateV2";
import { useReportForm } from "@components/ReportField/useReportForm";
import { SendClientToFronteraEhrButton } from "@components/SendClientToFronteraEhr/SendClientToFronteraEhrButton";
import { StatusDefinitionTooltip } from "@components/StatusLegendTooltip/StatusDefinitionTooltip";
import { StatusTag } from "@components/StatusTag/StatusTag";
import { WizardStepTracker } from "@components/analytics/WizardStepTracker";
import { useTrackReportOpened } from "@hooks/useReportAnalytics";
import {
  evaluationTrainTopStatusFromCurrentRoute,
  shouldUseHandlebarsExportPath,
} from "@pages/EvaluationDetails/helpers";
import { ClinicalReportDetailsProvider } from "@providers/ClinicalReportDetailsProvider";
import { ReportNode, useReport } from "@providers/ReportProvider";

export type EvaluationSubRoute =
  | "client-details"
  | "upload-files"
  | "report-part-1"
  | "report-part-2"
  | "conclusions"
  | "recommendations"
  | "appendix"
  | "review-and-sign";

const routeLabelMapping: { [key in EvaluationSubRoute]: string } = {
  "client-details": "Details",
  "upload-files": "Upload Files",
  "report-part-1": "Report Part 1",
  "report-part-2": "Report Part 2",
  conclusions: "Conclusions",
  recommendations: "Recommendations",
  appendix: "Appendix",
  "review-and-sign": "Review",
};

export const orderedEvaluationSubRoutes: EvaluationSubRoute[] = [
  "client-details",
  "upload-files",
  "report-part-1",
  "report-part-2",
  "conclusions",
  "recommendations",
  "review-and-sign",
];

const IA_EVALUATIONS_STEP_ORDER = [
  "client-details",
  "upload-files",
  "report-part-1",
  "report-part-2",
  "conclusions",
  "recommendations",
  "appendix",
  "review-and-sign",
];

export const getNextRoute = (route: EvaluationSubRoute): EvaluationSubRoute | null => {
  const { report } = useReport();
  const hasConclusionsFields = templateHasConclusionsFields(report?.reportTemplate);

  switch (route) {
    case "client-details":
      return "upload-files";
    case "upload-files":
      return "report-part-1";
    case "report-part-1":
      return "report-part-2";
    case "report-part-2":
      return hasConclusionsFields ? "conclusions" : "recommendations";
    case "conclusions":
      return "recommendations";
    case "recommendations":
      return "appendix";
    case "appendix":
      return "review-and-sign";
    case "review-and-sign":
      return "review-and-sign";
    default:
      return null;
  }
};

export const getPastRoute = (route: EvaluationSubRoute): EvaluationSubRoute | null => {
  const { report } = useReport();
  const hasConclusionsFields = templateHasConclusionsFields(report?.reportTemplate);

  switch (route) {
    case "upload-files":
      return "client-details";
    case "report-part-1":
      return "upload-files";
    case "report-part-2":
      return "report-part-1";
    case "conclusions":
      return "report-part-2";
    case "recommendations":
      return hasConclusionsFields ? "conclusions" : "report-part-2";
    case "appendix":
      return "recommendations";
    case "review-and-sign":
      return "appendix";
    case "client-details":
      return null;
    default:
      return null;
  }
};

export const EvaluationDetails: React.FC = () => {
  const { pathname, state } = useLocation();
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const { report, reportQuery } = useReport();

  const evaluationSections = (report?.reportSections?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => node != null) as ReportSectionsType[];
  const methods = useReportForm(evaluationSections);

  const response = useAssessmentToolsQuery(
    { reportType: ReportTypeEnums.DiagnosisEvaluation },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
  const supportedAssessmentsList: AssessmentToolType[] =
    response.data?.assessmentTools?.filter((item) => !!item).filter((tool) => tool.parsingSupported) ?? [];

  const files = report?.reportFiles?.edges?.map((edge) => edge?.node) || [];

  const templateId = report?.reportTemplate?.id;
  const templateName = report?.reportTemplate?.name;
  const reportContext = {
    report_id: evaluationId ?? "",
    report_type: "diagnosis_builder" as const,
    template_id: templateId,
    template_name: templateName,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  };
  useTrackReportOpened(reportContext);

  const filesForAssessment = files?.filter((file) => file?.status !== "PENDING")?.filter(nonNullable);
  const existingAssessmentsFound =
    filesForAssessment?.filter((file) => supportedAssessmentsList.map((a) => a.id).includes(file.fileKind)).length >= 1;

  const updateEvaluation = useUpdateReportMutation({});
  const reportId = report?.id;

  const { refetch: getExportReport, isLoading: isReportExporting } = useGetExportReportDocumentQuery(
    {
      reportId,
      exportType: ExportTypeEnum.Docx,
    },
    {
      enabled: false,
      retry: false,
    },
  );

  const { isPending } = updateEvaluation;

  const updateEvaluationStatus = async () => {
    await updateEvaluation
      .mutateAsync({
        reportId: report.id,
        status: ReportStatusEnums.Draft,
      })
      .then((data) => {
        if (data.updateReport?.status) {
          reportQuery.refetch();
        }
      });
  };

  const reportIsCompleted = report.reportStatus === ApiReportReportStatusChoices.Completed;

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as EvaluationSubRoute) || ("upload-files" as EvaluationSubRoute);

  const clientName = `${report?.reportClientDetails?.clientFirstName || "N/A"} ${report?.reportClientDetails?.clientLastName || ""}`;

  const headerStatus: StatusType = getReportLifecycleStatus(report);

  if (!orderedEvaluationSubRoutes.includes("appendix")) {
    const reviewAndSignIndex = orderedEvaluationSubRoutes.indexOf("review-and-sign");
    orderedEvaluationSubRoutes.splice(reviewAndSignIndex, 0, "appendix");
  }

  // Whether this report's template defines any Conclusions fields at all -- NOT an
  // org check. Derived per-render (never mutate the shared `orderedEvaluationSubRoutes`
  // export) because different reports for the same session/org can use different
  // templates, some with Conclusions fields and some without.
  const hasConclusionsFields = templateHasConclusionsFields(report?.reportTemplate);
  const effectiveOrderedSubRoutes = hasConclusionsFields
    ? orderedEvaluationSubRoutes
    : orderedEvaluationSubRoutes.filter((route) => route !== "conclusions");

  const blockUntilGenerating = (navStepRoute: string, report: ReportNode): boolean => {
    const generatingRoutes = ["report-part-1", "report-part-2", "conclusions", "recommendations"];

    return (
      generatingRoutes.includes(navStepRoute) &&
      report.reportSections.edges.filter((edge) => edge?.node?.pageLocation === navStepRoute).length === 0
    );
  };

  // log evaluation opening to datadog
  const base64DecodedId = atob(report.id);
  const databaseId = base64DecodedId.split("ReportType:")?.pop();
  datadogRum.addAction("report-opened", {
    reportId: databaseId,
    reportType: report.reportType,
    reportStatus: report.reportStatus,
  });

  const useHandlebarsExportPath = shouldUseHandlebarsExportPath(
    report.reportTemplate?.exportFramework,
    report.reportTemplate?.unifiedExport ?? false,
  );

  return (
    <>
      <WizardStepTracker reportContext={reportContext} stepOrder={IA_EVALUATIONS_STEP_ORDER} />
      <div className="flex w-full flex-col items-start justify-between">
        <div className="d-flex flex-col sticky top-20 pt-3  w-full z-20">
          <div className="-mt-10 flex flex-col gap-y-2 bg-white -mx-14 p-4 ">
            <div className="flex w-full items-center justify-between px-2">
              <SkeletonLoader loading={false}>
                <Paragraph colorType="primary" displayType="loud" className="flex items-center gap-2">
                  <span className="ph-no-capture">{clientName}</span> Evaluation
                  <StatusDefinitionTooltip status={headerStatus}>
                    <StatusTag status={headerStatus} />
                  </StatusDefinitionTooltip>
                  {report.reportTemplate?.name && <Badge text={report.reportTemplate.name} appearance="neutral" />}
                </Paragraph>
              </SkeletonLoader>
              {reportIsCompleted ? (
                <div className="flex items-center">
                  <Button
                    text="Edit"
                    disabled={isPending}
                    appearance="secondary"
                    onClick={() => updateEvaluationStatus()}
                    className="mr-2 ring-green-400"
                  />
                  {report.reportTemplate?.exportFramework === ApiReportTemplateExportFrameworkChoices.GoogleDocs ? (
                    <Button
                      text={isReportExporting ? "Exporting..." : "Export"}
                      disabled={isReportExporting}
                      appearance="primary"
                      onClick={() => {
                        getExportReport().then((queryResult) => {
                          const file = queryResult.data?.exportReportDocument?.file;
                          const fileName = queryResult.data?.exportReportDocument?.fileName;
                          downloadFileBlob(file, fileName);
                        });
                      }}
                    />
                  ) : useHandlebarsExportPath ? (
                    <ClinicalReportDetailsProvider reportId={report.id}>
                      <ClinicalReportExport
                        reportData={report}
                        refetchReportData={async () => {
                          await reportQuery.refetch();
                        }}
                        isReportLoading={reportQuery.isFetching}
                      />
                    </ClinicalReportDetailsProvider>
                  ) : (
                    <ExportWithTemplateV2
                      reportData={report}
                      refetchReport={async () => {
                        const result = await reportQuery.refetch();
                        return result.data?.getReports?.edges?.[0]?.node as ReportType | undefined;
                      }}
                    />
                  )}
                  <SendClientToFronteraEhrButton
                    reportId={report.id}
                    reportType={report.reportType}
                    isReportCompleted={reportIsCompleted}
                    learnerExternalId={report.learner?.externalId}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          {!reportIsCompleted ? (
            <div className="-mx-14 bg-background-secondary">
              <TrainStopProgressNav
                steps={effectiveOrderedSubRoutes.map((route, index) => {
                  const blockRoute = blockUntilGenerating(route, report);
                  const status = evaluationTrainTopStatusFromCurrentRoute(
                    currentRoute,
                    route,
                    report,
                    existingAssessmentsFound,
                  );
                  return {
                    index: index + 1,
                    name: routeLabelMapping[route],
                    status,
                    to: route,
                    navigationToEnabled: status !== "upcoming" && !blockRoute,
                    Component: ({ children, className, to }) => (
                      <>
                        {status === "upcoming" || blockRoute ? (
                          <span className={className}>{children}</span>
                        ) : (
                          <Link to={to} className={className} state={state}>
                            {children}
                          </Link>
                        )}
                      </>
                    ),
                  };
                })}
              />
            </div>
          ) : null}
        </div>
        <div className="w-full mt-4 py-8 h-full">
          <FormProvider {...methods}>
            <Outlet />
          </FormProvider>
        </div>
      </div>
    </>
  );
};
